import type {
  DisplayMinimumKey,
  DisplayMinimumView,
  DisplayMinimumWrite,
} from "@screener/protocol";
import { useCallback, useEffect, useState } from "react";
import { applyMinimumPut, applyMinimumRemove, type DisplayMinimumsState } from "./display-minimums";
import {
  fetchDisplayMinimums,
  removeDisplayMinimum,
  saveDisplayMinimum,
} from "./display-minimums-api";

/** Исход записи окна: `invalid` — не повторять, `unavailable` — повторить можно. */
export type DisplayMinimumOutcome = "ok" | "invalid" | "unavailable";

/**
 * Исход записи: при `ok` рядом лежит запись из ответа сервера (с порогом
 * скринера). Окно карточки решает по ней, закрываться ли; общему окну она не
 * нужна.
 */
export type DisplayMinimumPutResult =
  | { outcome: "ok"; minimum: DisplayMinimumView }
  | { outcome: "invalid" | "unavailable" };

interface UseDisplayMinimumsOptions {
  onSessionInvalid: () => void;
}

export interface UseDisplayMinimumsResult {
  state: DisplayMinimumsState;
  /** Завести или заменить минимум. Ответ сервера ложится в список без перечитывания. */
  put: (write: DisplayMinimumWrite) => Promise<DisplayMinimumPutResult>;
  remove: (key: DisplayMinimumKey) => Promise<DisplayMinimumOutcome>;
  /** Перечитать список — кнопка «Повторить» окна, когда чтение не удалось. */
  reload: () => Promise<void>;
}

/**
 * Личные минимумы §59 общим списком пользователя. Один экземпляр на каркас
 * (`app-shell.tsx`): окно правит список, экран плотностей читает его же и
 * отбирает строки сразу, без второго запроса.
 */
export function useDisplayMinimums({
  onSessionInvalid,
}: UseDisplayMinimumsOptions): UseDisplayMinimumsResult {
  const [state, setState] = useState<DisplayMinimumsState>({ status: "loading" });

  const reload = useCallback(async (): Promise<void> => {
    setState((previous) => (previous.status === "ready" ? previous : { status: "loading" }));
    const result = await fetchDisplayMinimums();
    if (result.status === "unauthorized") {
      // Сессия протухла на первом чтении: список не прочитан, экран говорит
      // «недоступны», а не висит в «читаем». Прочитанный список не трогаем.
      setState((previous) => (previous.status === "ready" ? previous : { status: "unavailable" }));
      onSessionInvalid();
      return;
    }
    setState(
      result.status === "ok"
        ? { status: "ready", minimums: result.minimums }
        : { status: "unavailable" },
    );
  }, [onSessionInvalid]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const put = useCallback(
    async (write: DisplayMinimumWrite): Promise<DisplayMinimumPutResult> => {
      const result = await saveDisplayMinimum(write);
      if (result.status === "unauthorized") {
        onSessionInvalid();
        return { outcome: "unavailable" };
      }
      if (result.status === "ok") {
        setState((previous) => applyMinimumPut(previous, result.minimum));
        return { outcome: "ok", minimum: result.minimum };
      }
      return { outcome: result.status };
    },
    [onSessionInvalid],
  );

  const remove = useCallback(
    async (key: DisplayMinimumKey): Promise<DisplayMinimumOutcome> => {
      const result = await removeDisplayMinimum(key);
      if (result.status === "unauthorized") {
        onSessionInvalid();
        return "unavailable";
      }
      if (result.status === "ok") {
        setState((previous) => applyMinimumRemove(previous, key));
        return "ok";
      }
      return result.status;
    },
    [onSessionInvalid],
  );

  return { state, put, remove, reload };
}
